"use client"

import { useState, useRef, useEffect } from "react"
import { UserIcon, Loader2, Camera, Mail, Phone, MapPin, Lock, CheckCircle } from "lucide-react"
import { getTeacherData } from "./api"
import { generatePresignedUrl, postTeacherProfilePhoto } from "../postFileAws"
import { postAws } from "../postAws"

const EditTeacherProfile = () => {
  const [teacher, setTeacher] = useState({
    teacherId: "",
    teacherName: "",
    teacherEmail: "",
    phoneNumber: "",
    teacherAddress: "",
    profilePhotoS3Uri: "",
  })
  const [isEditing, setIsEditing] = useState(false)
  const [profileImage, setProfileImage] = useState("")
  const [imagePreview, setImagePreview] = useState("")
  const fileInputRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [passwordChangeStep, setPasswordChangeStep] = useState("request") // 'request', 'verify', 'change'
  const [otp, setOtp] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const [passwordChangeError, setPasswordChangeError] = useState("")
  const [isOtpSending, setIsOtpSending] = useState(false)
  const [isPasswordChanging, setIsPasswordChanging] = useState(false)
  const [showSuccessFlashcard, setShowSuccessFlashcard] = useState(false)

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        setIsLoading(true)
        const teacherId = localStorage.getItem("teacherId")
        if (!teacherId) return

        const response = await getTeacherData(teacherId)
        if (response?.statusCode === 200 || response?.statusCode === 201) {
          const data = response.body.data
          setTeacher({ ...data, teacherId })

          if (data.profilePhotoS3Uri) {
            const presignedUrl = await generatePresignedUrl(data.profilePhotoS3Uri)
            setProfileImage(presignedUrl)
            setImagePreview(presignedUrl)
          }
        }
      } catch (error) {
        console.error("Error fetching teacher data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTeacherData()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setTeacher((prevTeacher) => ({
      ...prevTeacher,
      [name]: value,
    }))
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing)
  }

  const handleImageClick = () => {
    if (isEditing) {
      fileInputRef.current.click()
    }
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      setImagePreview(URL.createObjectURL(file))
      setProfileImage(file)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const updatedTeacher = { ...teacher }

      if (profileImage instanceof File) {
        const uploadResult = await postTeacherProfilePhoto(profileImage, teacher.teacherId, profileImage.name)
        if (uploadResult && uploadResult.uri) {
          updatedTeacher.profilePhotoS3Uri = uploadResult.uri
        }
      }

      const apiData = {
        request: "update-teacher-profile",
        body: {
          teacherId: updatedTeacher.teacherId,
          teacherName: updatedTeacher.teacherName,
          teacherEmail: updatedTeacher.teacherEmail,
          phoneNumber: updatedTeacher.phoneNumber,
          teacherAddress: updatedTeacher.teacherAddress,
          profilePhotoS3Uri: updatedTeacher.profilePhotoS3Uri,
        },
      }

      Object.keys(apiData.body).forEach((key) => apiData.body[key] === undefined && delete apiData.body[key])

      const response = await postAws("https://api-dev.aurait.ai/auth/teacher-auth", "POST", apiData)

      if (response && response.statusCode === 200) {
        console.log("Profile updated successfully")
        setTeacher(updatedTeacher)
        setIsEditing(false)
        setShowSuccessFlashcard(true)
        setTimeout(() => setShowSuccessFlashcard(false), 5000)
      } else {
        throw new Error("Failed to update profile")
      }
    } catch (error) {
      console.error("Error saving changes:", error)
      alert("Failed to save changes. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleRequestPasswordChange = async () => {
    setPasswordChangeError("")
    setIsOtpSending(true)
    try {
      const response = await postAws("https://api-dev.aurait.ai/forgotpassword/teacher", "POST", {
        body: { teacherEmail: teacher.teacherEmail },
        request: "forgot-password-Teacher",
      })

      if (response.statusCode === 200) {
        setPasswordChangeStep("verify")
      } else {
        throw new Error("Failed to send OTP")
      }
    } catch (error) {
      setPasswordChangeError("Failed to send OTP. Please try again.")
    } finally {
      setIsOtpSending(false)
    }
  }

  const handleVerifyOtp = async () => {
    setPasswordChangeError("")
    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError("Passwords do not match")
      return
    }
    setIsPasswordChanging(true)
    try {
      const response = await postAws("https://api-dev.aurait.ai/forgotpassword/teacher", "POST", {
        body: {
          teacherEmail: teacher.teacherEmail,
          otp: otp,
          password: newPassword,
        },
        request: "verify-otp-Teacher",
      })

      if (response.statusCode === 201) {
        setShowChangePassword(false)
        setPasswordChangeStep("request")
        setShowSuccessFlashcard(true)
        setTimeout(() => setShowSuccessFlashcard(false), 5000) // Hide flashcard after 5 seconds
      } else {
        throw new Error("Failed to change password")
      }
    } catch (error) {
      setPasswordChangeError("Failed to change password. Please try again.")
    } finally {
      setIsPasswordChanging(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4 sm:py-12 sm:px-6 lg:px-8 -mt-5">
      {/* Success notification */}
      {showSuccessFlashcard && (
        <div className="fixed top-4 right-4 z-50 animate-in fade-in slide-in-from-top-5 duration-300">
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md shadow-lg flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
            <span className="font-medium">Success!</span>
            <span className="ml-2">
              {passwordChangeStep === "verify" ? "Password changed successfully!" : "Profile updated successfully!"}
            </span>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Profile header for mobile */}
        <div className="md:hidden bg-blue-600 p-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="relative">
              {profileImage ? (
                <div className="relative w-20 h-20">
                  <img
                    src={imagePreview || profileImage}
                    alt="Teacher"
                    className="w-20 h-20 rounded-full border-2 border-white shadow-md object-cover"
                  />
                  {isEditing && (
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full cursor-pointer"
                      onClick={handleImageClick}
                    >
                      <Camera className="h-6 w-6 text-white" />
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={`w-20 h-20 rounded-full border-2 border-white shadow-md flex items-center justify-center bg-gray-200 ${isEditing ? "cursor-pointer" : ""}`}
                  onClick={isEditing ? handleImageClick : undefined}
                >
                  <UserIcon className="w-10 h-10 text-gray-400" />
                  {isEditing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                      <Camera className="h-6 w-6 text-white" />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">{teacher.teacherName || "No name provided"}</h2>
              <p className="text-sm text-white/80 flex items-center mt-1">
                <Mail className="h-3 w-3 mr-1" />
                {teacher.teacherEmail || "No email provided"}
              </p>
            </div>
          </div>
        </div>

        <div className="md:flex">
          {/* Left sidebar - hidden on mobile */}
          <div className="hidden md:block md:w-1/3 bg-blue-600 p-8 text-white">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="relative">
                  {profileImage ? (
                    <div className="relative">
                      <img
                        src={imagePreview || profileImage}
                        alt="Teacher"
                        className="w-36 h-36 rounded-full border-4 border-white shadow-lg mx-auto mb-4 object-cover"
                      />
                      {isEditing && (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full cursor-pointer"
                          onClick={handleImageClick}
                        >
                          <Camera className="h-8 w-8 text-white" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`w-36 h-36 rounded-full border-4 border-white shadow-lg mx-auto mb-4 flex items-center justify-center bg-gray-200 relative ${isEditing ? "cursor-pointer" : ""}`}
                      onClick={isEditing ? handleImageClick : undefined}
                    >
                      <UserIcon className="w-16 h-16 text-gray-400" />
                      {isEditing && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                          <Camera className="h-8 w-8 text-white" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
              <h2 className="text-2xl font-bold mb-1">{teacher.teacherName || "No name provided"}</h2>
              <p className="text-sm text-white/80 flex items-center justify-center">
                <Mail className="h-4 w-4 mr-1" />
                {teacher.teacherEmail || "No email provided"}
              </p>
            </div>

            <div className="mt-10 space-y-4">
              <h3 className="text-lg font-semibold border-b border-white/20 pb-2 mb-4">Contact Information</h3>

              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-white/70 mt-0.5" />
                <div>
                  <p className="text-sm text-white/70">Phone</p>
                  <p className="font-medium">{teacher.phoneNumber || "No phone number provided"}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-white/70 mt-0.5" />
                <div>
                  <p className="text-sm text-white/70">Address</p>
                  <p className="font-medium">{teacher.teacherAddress || "No address provided"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="p-6 md:p-8 md:w-2/3">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                {isEditing ? "Edit Profile" : "Teacher Profile"}
              </h1>
              <button
                className="px-4 py-2 text-white bg-blue-600 rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md transition-all"
                onClick={handleEditToggle}
              >
                {isEditing ? "Cancel" : "Edit Profile"}
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="teacherName"
                      value={teacher.teacherName}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <p className="text-lg text-gray-800 p-3 bg-gray-50 rounded-lg">
                      {teacher.teacherName || "No name provided"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={teacher.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <p className="text-lg text-gray-800 p-3 bg-gray-50 rounded-lg">
                      {teacher.phoneNumber || "No phone number provided"}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                {isEditing ? (
                  <input
                    type="email"
                    name="teacherEmail"
                    value={teacher.teacherEmail}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="Enter your email address"
                  />
                ) : (
                  <p className="text-lg text-gray-800 p-3 bg-gray-50 rounded-lg">
                    {teacher.teacherEmail || "No email provided"}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                {isEditing ? (
                  <textarea
                    name="teacherAddress"
                    value={teacher.teacherAddress}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="Enter your address"
                  />
                ) : (
                  <p className="text-lg text-gray-800 p-3 bg-gray-50 rounded-lg min-h-[80px]">
                    {teacher.teacherAddress || "No address provided"}
                  </p>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {isEditing && (
                <button
                  className="px-6 py-3 text-white bg-green-600 rounded-full hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-md transition-all flex-1 flex justify-center items-center"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="animate-spin h-5 w-5 mr-2" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              )}

              <button
                className="px-6 py-3 text-white bg-orange-500 rounded-full hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-md transition-all flex-1 flex justify-center items-center"
                onClick={() => setShowChangePassword(!showChangePassword)}
              >
                <Lock className="h-5 w-5 mr-2" />
                {showChangePassword ? "Cancel Password Change" : "Change Password"}
              </button>
            </div>

            {/* Password change section */}
            {showChangePassword && (
              <div className="mt-6 p-6 border border-gray-200 rounded-xl bg-gray-50">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Lock className="h-5 w-5 mr-2 text-blue-600" />
                  Change Password
                </h3>

                {passwordChangeStep === "request" && (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      To change your password, we'll send a one-time password (OTP) to your email address.
                    </p>
                    <button
                      className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-all"
                      onClick={handleRequestPasswordChange}
                      disabled={isOtpSending}
                    >
                      {isOtpSending ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Sending OTP...
                        </>
                      ) : (
                        "Request OTP"
                      )}
                    </button>
                  </div>
                )}

                {passwordChangeStep === "verify" && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      handleVerifyOtp()
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Enter OTP</label>
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder="Enter the OTP sent to your email"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Please check your email for the OTP we just sent.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder="Enter new password"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder="Confirm new password"
                        required
                      />
                    </div>

                    {passwordChangeError && (
                      <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                        {passwordChangeError}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <button
                        type="button"
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex-1 flex justify-center items-center"
                        onClick={() => setPasswordChangeStep("request")}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex-1 flex justify-center items-center"
                        disabled={isPasswordChanging}
                      >
                        {isPasswordChanging ? (
                          <>
                            <Loader2 className="animate-spin h-5 w-5 mr-2" />
                            Changing Password...
                          </>
                        ) : (
                          "Change Password"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditTeacherProfile

